import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import "./Form.scss";
import PageLayout from "../../component/pagelayout/PageLayout";
import { Text } from "../../component/text/Text";
import Button from "../../component/button/Button";
import { DEFAULT_API_CONFIG } from "../../api/api-config";
import { digiThemeUuid } from "../../constants/Uuid";
import { UserContext } from "../../context/UserContext";

const Form = () => {
  const reguiredFields = useMemo(() => ["osnid", "osnid_description"], []);
  const [showConfirmMessage, setShowConfirmMessage] = useState<boolean>(false);
  const confirmMessage = useMemo<string>(() => {
    return "Skjemaet er sendt!";
  }, []);

  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const errorMessage = useMemo<string>(() => {
    return "En feil har skjedd.. (husk: namespace id og description må være unike)";
  }, []);

  const [showRequiredMessage, setShowRequiredMessage] =
    useState<boolean>(false);
  const requiredMessage = useMemo<string>(() => {
    return "Fyll ut de påkrevde feltene!";
  }, []);

  const [missingFields, setMissingFields] = useState<string[]>([]);

  const { isLoggedIn } = useContext(UserContext);

  const formElements = useMemo(
    () => [
      {
        type: "text",
        name: "Organizational specific namespace ID",
        placeholder: "Organizational specific namespace ID",
        key: "osnid",
      },
      {
        type: "text",
        name: "Description OSNID",
        placeholder: "Description OSNID",
        key: "osnid_description",
      },
      {
        type: "text",
        name: "Available formats",
        placeholder: "Available formats",
        key: "available_format",
      },
      {
        type: "text",
        name: "Resource location",
        placeholder: "Resource location",
        key: "resource_location",
      },
      {
        type: "text",
        name: "Namespace owner",
        placeholder: "Namespace owner",
        key: "namespace_owner",
      },
      {
        type: "text",
        name: "Local ID",
        placeholder: "Local id",
        key: "local_id",
      },
      {
        type: "text",
        name: "Version tag",
        placeholder: "Version tag",
        key: "version_tag",
      },
    ],
    []
  );

  const [inputValues, setInputValues] = useState(
    formElements.reduce(
      (acc, element) => {
        acc[element.key] = "";
        return acc;
      },
      {} as { [key: string]: string }
    )
  );

  const [placeholders, setPlaceholders] = useState(
    formElements.map((element) => element.placeholder)
  );

  const handleFocus = useCallback(
    (index: number) => {
      const newPlaceholders = [...placeholders];
      newPlaceholders[index] = "";
      setPlaceholders(newPlaceholders);
    },
    [placeholders]
  );

  const handleBlur = useCallback(
    (index: number, originalPlaceholder: string) => {
      const newPlaceholders = [...placeholders];
      newPlaceholders[index] = originalPlaceholder;
      setPlaceholders(newPlaceholders);
    },
    [placeholders]
  );

  const formSubmitUrl = useMemo<string>(() => {
    return DEFAULT_API_CONFIG.url + `/features/${digiThemeUuid}`;
  }, []);

  const handleChangeFormInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
      const newInputValues = { ...inputValues };
      newInputValues[formElements[index].key] = event.target.value;
      setInputValues(newInputValues);
    },
    [inputValues, formElements]
  );

  const handleSetRequiredClassname = useCallback(
    (key: string) => {
      return missingFields.includes(key) ? "required" : "";
    },
    [missingFields]
  );

  const handleFormSubmit = useCallback(async () => {
    if (reguiredFields.some((field) => !inputValues[field])) {
      setMissingFields(reguiredFields.filter((field) => !inputValues[field]));
      setShowConfirmMessage(false);
      setShowErrorMessage(false);
      setShowRequiredMessage(true);
      return;
    }
    try {
      const response = await fetch(formSubmitUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ data: inputValues }),
      });
      if (response.status === 500) {
        setShowConfirmMessage(false);
        setShowRequiredMessage(false);
        setShowErrorMessage(true);
        return;
      }
      setInputValues(
        formElements.reduce(
          (acc, element) => {
            acc[element.key] = "";
            return acc;
          },
          {} as { [key: string]: string }
        )
      );
      setMissingFields([]);
      setShowErrorMessage(false);
      setShowRequiredMessage(false);
      setShowConfirmMessage(true);
    } catch (error) {
      console.log("Error:", error);
    }
  }, [formElements, formSubmitUrl, inputValues, reguiredFields]);

  useEffect(() => {
    if (!isLoggedIn) {
      window.location.href = "/login";
    }
  }, [isLoggedIn]);

  return (
    <PageLayout>
      <div className="form">
        <div className="form__title">
          <Text text="Skjema" size="xs" fontWeight={600} />
        </div>
        <div className="form__content">
          {formElements.map((element, index) => (
            <div className="form__content__pair" key={index}>
              <Text
                className="form__content__pair__text"
                text={element.name}
                size="xxs"
              />
              <input
                className={`form__content__pair__input ${handleSetRequiredClassname(element.key)}`}
                type={element.type}
                placeholder={placeholders[index]}
                value={inputValues[element.key]}
                onChange={(event) => handleChangeFormInput(event, index)}
                onFocus={() => handleFocus(index)}
                onBlur={() => handleBlur(index, element.placeholder)}
              />
            </div>
          ))}
          {showErrorMessage && (
            <Text className="error-message" text={errorMessage} size="xxs" />
          )}
          {showConfirmMessage && (
            <Text
              className="confirm-message"
              text={confirmMessage}
              size="xxs"
            />
          )}
          {showRequiredMessage && (
            <Text
              className="required-message"
              text={requiredMessage}
              size="xxs"
            />
          )}
          <div className="form__content__button">
            <Button text="Send" onClick={handleFormSubmit} />
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Form;
